import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MainLayout from '../components/layout/MainLayout';

const AGENTS_API = `${typeof window !== 'undefined' ? window.location.origin : ''}/api/agents`;

const emptyForm = {
  symbol: 'NVDA',
  asset_class: 'equity',
  entry_price: '',
  target_pct: '1.20',
  leverage_sim: '1',
  exit_mode: 'aviso',
  note: '',
};

export default function Agents() {
  const [health, setHealth] = useState(null);
  const [arch, setArch] = useState(null);
  const [specs, setSpecs] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [quote, setQuote] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [symbolQuery, setSymbolQuery] = useState('NVDA');
  const [showSuggest, setShowSuggest] = useState(false);
  const suggestRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [h, a, s] = await Promise.all([
        fetch(`${AGENTS_API}/health`).then((r) => r.json()),
        fetch(`${AGENTS_API}/architecture`).then((r) => r.json()),
        fetch(`${AGENTS_API}/sandbox/specs`).then((r) => r.json()),
      ]);
      setHealth(h);
      setArch(a);
      setSpecs(s.specs || []);
    } catch (e) {
      setMsg(`Erro ao carregar: ${e.message}`);
    }
  }, []);

  const loadCatalog = useCallback(async (assetClass) => {
    try {
      const params = new URLSearchParams({
        asset_class: assetClass,
        limit: '80',
      });
      const data = await fetch(`${AGENTS_API}/markets/symbols?${params}`).then(async (r) => {
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || JSON.stringify(j));
        return j;
      });
      setCatalog(data.symbols || []);
    } catch (e) {
      setMsg(`Catálogo: ${e.message}`);
      setCatalog([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadCatalog(form.asset_class);
  }, [form.asset_class, loadCatalog]);

  useEffect(() => {
    const onDocClick = (ev) => {
      if (suggestRef.current && !suggestRef.current.contains(ev.target)) {
        setShowSuggest(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const q = symbolQuery.trim().toLowerCase();
    if (!q) return catalog.slice(0, 12);
    return catalog
      .filter((row) => {
        const hay = `${row.symbol} ${row.name} ${row.label}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 12);
  }, [catalog, symbolQuery]);

  const onChange = (e) => {
    const { name, value } = e.target;
    if (name === 'asset_class') {
      setForm((f) => ({ ...f, asset_class: value, symbol: '', entry_price: '' }));
      setSymbolQuery('');
      setQuote(null);
      setShowSuggest(true);
      return;
    }
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSymbolInput = (e) => {
    const value = e.target.value;
    setSymbolQuery(value);
    setForm((f) => ({ ...f, symbol: value.trim().toUpperCase() }));
    setShowSuggest(true);
  };

  const pickSymbol = (row) => {
    setForm((f) => ({
      ...f,
      symbol: row.symbol,
      asset_class: row.asset_class || f.asset_class,
      entry_price: '',
    }));
    setSymbolQuery(row.symbol);
    setShowSuggest(false);
    setQuote(null);
  };

  const fetchQuote = async () => {
    setBusy(true);
    setMsg('');
    try {
      const q = await fetch(
        `${AGENTS_API}/markets/quote?symbol=${encodeURIComponent(form.symbol)}&asset_class=${form.asset_class}`
      ).then(async (r) => {
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || JSON.stringify(j));
        return j;
      });
      setQuote(q);
      setForm((f) => ({ ...f, entry_price: String(q.price) }));
      setMsg(`Cotação ${q.symbol}: ${q.price} (${q.yahoo_symbol})`);
    } catch (e) {
      setMsg(`Quote falhou: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const createSpec = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    try {
      const body = {
        symbol: form.symbol,
        asset_class: form.asset_class,
        entry_price: Number(form.entry_price),
        target_pct: Number(form.target_pct),
        leverage_sim: Number(form.leverage_sim),
        exit_mode: form.exit_mode,
        note: form.note,
      };
      const res = await fetch(`${AGENTS_API}/sandbox/specs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then(async (r) => {
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || JSON.stringify(j));
        return j;
      });
      setMsg(`Spec criada: ${res.id} · alvo ${res.target_price}`);
      await load();
    } catch (err) {
      setMsg(`Criar falhou: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const tickLive = async (id) => {
    setBusy(true);
    try {
      const res = await fetch(`${AGENTS_API}/sandbox/specs/${id}/tick_live`, {
        method: 'POST',
      }).then(async (r) => {
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || JSON.stringify(j));
        return j;
      });
      setMsg(`tick_live ${res.symbol}: ${res.current_price} → ${res.status} (pnl ${res.pnl_pct}%)`);
      await load();
    } catch (e) {
      setMsg(`tick_live: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const simulate = async (id) => {
    setBusy(true);
    try {
      const res = await fetch(`${AGENTS_API}/sandbox/specs/${id}/simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ steps: 20, step_pct: 0.2 }),
      }).then(async (r) => {
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || JSON.stringify(j));
        return j;
      });
      setMsg(`simulate → ${res.status} pnl ${res.pnl_pct}%`);
      await load();
    } catch (e) {
      setMsg(`simulate: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const confirmClose = async (id) => {
    setBusy(true);
    try {
      const res = await fetch(`${AGENTS_API}/sandbox/specs/${id}/confirm_close`, {
        method: 'POST',
      }).then(async (r) => {
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || JSON.stringify(j));
        return j;
      });
      setMsg(`confirm_close → ${res.status}`);
      await load();
    } catch (e) {
      setMsg(`confirm: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const openAlice = health?.openalice_ui || 'https://openalice.myvexx.com';

  return (
    <MainLayout>
      <div className="container-xl py-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h2 className="mb-1">Agents / Trade Specs</h2>
            <div className="text-muted">
              Invest (regras) + OpenAlice (investigação) + sandbox paper · opção 2: preço real
            </div>
          </div>
          <div className="d-flex gap-2">
            <a className="btn btn-outline-primary" href={openAlice} target="_blank" rel="noreferrer">
              Abrir OpenAlice
            </a>
            <button type="button" className="btn btn-secondary" onClick={load} disabled={busy}>
              Refresh
            </button>
          </div>
        </div>

        {msg && (
          <div className="alert alert-info" role="alert">
            {msg}
          </div>
        )}

        <div className="row g-3 mb-3">
          <div className="col-md-4">
            <div className="card card-sm">
              <div className="card-body">
                <div className="text-muted">Bridge</div>
                <div>
                  {health?.ok ? 'OK' : '…'} · paper · live={String(health?.allow_live_broker)}
                </div>
                <div className="small text-muted">specs sandbox: {health?.sandbox_specs ?? '—'}</div>
              </div>
            </div>
          </div>
          <div className="col-md-8">
            <div className="card card-sm">
              <div className="card-body">
                <div className="text-muted">Fluxo</div>
                <ol className="mb-0 small">
                  {(arch?.flow || []).map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header">
            <h3 className="card-title">Nova spec (alvo % → voltar à base)</h3>
          </div>
          <div className="card-body">
            <form className="row g-2" onSubmit={createSpec}>
              <div className="col-md-2">
                <label className="form-label">Classe</label>
                <select className="form-select" name="asset_class" value={form.asset_class} onChange={onChange}>
                  <option value="equity">equity</option>
                  <option value="crypto">crypto</option>
                  <option value="index">index</option>
                  <option value="fx">fx</option>
                </select>
              </div>
              <div className="col-md-3" ref={suggestRef} style={{ position: 'relative' }}>
                <label className="form-label">Símbolo</label>
                <input
                  className="form-control"
                  name="symbol"
                  value={symbolQuery}
                  onChange={onSymbolInput}
                  onFocus={() => setShowSuggest(true)}
                  placeholder="Escreve ou escolhe…"
                  autoComplete="off"
                  required
                />
                {showSuggest && filtered.length > 0 && (
                  <div
                    className="list-group"
                    style={{
                      position: 'absolute',
                      zIndex: 20,
                      left: 0,
                      right: 0,
                      maxHeight: 240,
                      overflowY: 'auto',
                      boxShadow: '0 8px 24px rgba(0,0,0,.12)',
                    }}
                  >
                    {filtered.map((row) => (
                      <button
                        key={`${row.asset_class}-${row.symbol}`}
                        type="button"
                        className="list-group-item list-group-item-action py-2"
                        onClick={() => pickSymbol(row)}
                      >
                        <strong>{row.symbol}</strong>
                        <span className="text-muted ms-2 small">{row.name}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="form-hint small text-muted mt-1">
                  Base: {catalog.length} símbolos nesta vertical
                </div>
              </div>
              <div className="col-md-2">
                <label className="form-label">Entrada</label>
                <input
                  className="form-control"
                  name="entry_price"
                  value={form.entry_price}
                  onChange={onChange}
                  required
                  type="number"
                  step="any"
                />
              </div>
              <div className="col-md-1">
                <label className="form-label">Alvo %</label>
                <input
                  className="form-control"
                  name="target_pct"
                  value={form.target_pct}
                  onChange={onChange}
                  type="number"
                  step="0.01"
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Alavanc. sim</label>
                <input
                  className="form-control"
                  name="leverage_sim"
                  value={form.leverage_sim}
                  onChange={onChange}
                  type="number"
                  step="0.1"
                  min="1"
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Modo saída</label>
                <select className="form-select" name="exit_mode" value={form.exit_mode} onChange={onChange}>
                  <option value="aviso">aviso</option>
                  <option value="semi">semi</option>
                  <option value="auto_paper">auto_paper</option>
                </select>
              </div>
              <div className="col-12">
                <input
                  className="form-control"
                  name="note"
                  value={form.note}
                  onChange={onChange}
                  placeholder="Nota / tese (opcional)"
                />
              </div>
              <div className="col-12 d-flex gap-2">
                <button type="button" className="btn btn-outline-secondary" onClick={fetchQuote} disabled={busy}>
                  Cotação real → entrada
                </button>
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  Criar spec paper
                </button>
                {quote && (
                  <span className="align-self-center small text-muted">
                    last quote {quote.price} {quote.currency}
                  </span>
                )}
              </div>
            </form>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Specs activas</h3>
          </div>
          <div className="table-responsive">
            <table className="table table-vcenter card-table">
              <thead>
                <tr>
                  <th>Símbolo</th>
                  <th>Modo</th>
                  <th>Entrada → actual</th>
                  <th>Alvo</th>
                  <th>PnL %</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {specs.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-muted">
                      Nenhuma spec ainda.
                    </td>
                  </tr>
                )}
                {specs
                  .slice()
                  .reverse()
                  .map((s) => (
                    <tr key={s.id}>
                      <td>
                        <strong>{s.symbol}</strong>
                        <div className="small text-muted">{s.asset_class}</div>
                      </td>
                      <td>{s.exit_mode}</td>
                      <td>
                        {s.entry_price} → {s.current_price}
                      </td>
                      <td>
                        {s.target_pct}% ({s.target_price})
                      </td>
                      <td>
                        {s.pnl_pct}
                        {s.leverage_sim > 1 ? ` · ×${s.leverage_sim}=${s.pnl_pct_levered}` : ''}
                      </td>
                      <td>
                        <span className="badge bg-blue-lt">{s.status}</span>
                      </td>
                      <td className="text-end">
                        <div className="btn-list flex-nowrap">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            disabled={busy}
                            onClick={() => tickLive(s.id)}
                          >
                            tick_live
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={busy}
                            onClick={() => simulate(s.id)}
                          >
                            simulate
                          </button>
                          {s.status === 'ready_to_close' && (
                            <button
                              type="button"
                              className="btn btn-sm btn-success"
                              disabled={busy}
                              onClick={() => confirmClose(s.id)}
                            >
                              confirm_close
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
